/**
 * Order types for the partner-facing Orders page. Mirrors the subset of the
 * reware-api `orders`/`order` admin query selection used by reware-admin's
 * Orders portal (orders-api.js).
 */
import { z } from 'zod';

import type { RewareAdminTypes } from '@troverecommerce/shared-reware-api';

import type { ConstantOption } from '@src/components/indexPage';

export type { ConstantOption };

/**
 * One address field.
 *
 * Accepts a number as well as a string, because rejecting one costs more than keeping it.
 * `delivery_address` is JSONB written as a spread of caller-supplied data (reware-api
 * OrderService), so nothing enforces that `postalCode` or `phone` arrive as strings — and a
 * strict `z.string()` turns a single numeric postcode into a failed parse for the whole address.
 * That is not a cosmetic difference: a failed parse blanks the Edit shipping address dialog, and
 * the server merges what that dialog sends over the stored address, so an avoidable rejection
 * becomes a way to erase a real address.
 *
 * A nested object or array still fails, which is the case that genuinely means "we cannot read
 * this".
 */
const addressField = z.union([z.string(), z.number()]).transform(String).nullish();

/**
 * `deliveryAddress` is a JSON scalar on OrderAdmin, selected without a sub-selection, so the
 * schema tells us nothing about its contents. Parsed rather than asserted: an interface here
 * would be a guess the compiler then treats as fact.
 *
 * That guess has a cost. If the shape differs — keys absent rather than null, or the scalar
 * arriving as a JSON string — an asserted type still reads as truthy, so the Customer column
 * renders an em dash on every row and the shipping section empties out. The operator is told a
 * fact about the customer's data that is really a parsing failure.
 *
 * Everything is optional because nothing guarantees otherwise. Unknown keys are dropped rather
 * than rejected.
 */
export const deliveryAddressSchema = z.object({
  name: addressField,
  firstName: addressField,
  lastName: addressField,
  address1: addressField,
  address2: addressField,
  city: addressField,
  state: addressField,
  postalCode: addressField,
  country: addressField,
  email: addressField,
  phone: addressField,
});

export type DeliveryAddress = z.infer<typeof deliveryAddressSchema>;

/**
 * Narrow an unparsed `deliveryAddress` to the fields the page reads.
 *
 * Returns null when the value is absent or not an object, which callers must distinguish from a
 * parsed address whose fields are empty — the first means "we could not read this", the second
 * means "there is nothing recorded".
 */
export const parseDeliveryAddress = (value: unknown): DeliveryAddress | null => {
  if (value == null || typeof value !== 'object') return null;
  const parsed = deliveryAddressSchema.safeParse(value);
  if (!parsed.success) {
    console.error('Order delivery address did not match the expected shape', {
      issues: parsed.error.issues,
    });
    return null;
  }
  return parsed.data;
};

/**
 * Wire types derived from the generated admin schema (`RewareAdminTypes`, regenerated from
 * reware-api) rather than hand-transcribed. The hand-written versions these replace disagreed
 * with the schema on five scalars — `taxLines.price` as a number when it is a String, the
 * tri-state filters as strings when they are Ints, `ccLast4` as a string when it is an Int, and
 * the page envelope as non-null when every field is nullable — and each disagreement shipped as
 * a runtime bug rather than a compile error.
 *
 * Only deliberate narrowings are spelled out below. Anything aliased straight across is the
 * generated type verbatim, so a schema change surfaces here as a type error.
 */
export type TaxLine = RewareAdminTypes.TaxLineTypeAdmin;
export type PromotionDiscount = RewareAdminTypes.PromotionDiscountTypeAdmin;
export type OrderStateTransition = RewareAdminTypes.StateTransitionsAdmin;
export type Ypackage = RewareAdminTypes.YpackageAdmin;
export type Escalation = RewareAdminTypes.EscalationAdmin;
export type OrderEmail = RewareAdminTypes.EmailHistoryAdmin;
export type OrderItemDiscount = RewareAdminTypes.DiscountAdmin;
export type OrderItemRefund = RewareAdminTypes.RefundAdmin;

/**
 * A gift card tendered against the order, narrowed to the two fields the query selects.
 *
 * Deliberately NOT the whole `GiftcardAdmin`, which declares `number: String!`. The full number
 * is sensitive — see the note on the paymentDetails selection in fetchOrders.ts — and picking
 * only these two means the type system refuses any attempt to read it from this page rather than
 * leaving it one autocomplete away.
 */
export type GiftCardAmount = Pick<RewareAdminTypes.GiftcardAdmin, 'lastFour' | 'amount'>;

/** Payment details with the gift card lists narrowed to {@link GiftCardAmount}. */
export type PaymentDetails = Omit<
  RewareAdminTypes.PaymentDetailsTypeAdmin,
  'paymentGiftCards' | 'refundedGiftCards' | 'csGiftCards' | 'failedGiftCards'
> & {
  paymentGiftCards?: (GiftCardAmount | null)[] | null;
  refundedGiftCards?: (GiftCardAmount | null)[] | null;
  csGiftCards?: (GiftCardAmount | null)[] | null;
};

/**
 * `imageUrls` is a JSON scalar, so the generated type is `any`. Partial because a given position
 * may be absent, which OrderItems relies on when it falls back to the first available image.
 */
export type ImageUrlMap = Partial<Record<string, string>>;

/**
 * Narrow the `imageUrls` scalar, dropping any entry that is not a string URL.
 *
 * Asserted rather than parsed, this had the same failure mode the deliveryAddress parse exists to
 * prevent: if the scalar arrives as a JSON *string*, `inv.imageUrls` is truthy, `.front` is
 * undefined, and `Object.values(...)[0]` is the first character — so the card renders
 * `<img src="h">` instead of the no-image placeholder, with nothing logged.
 */
export const parseImageUrls = (value: unknown): ImageUrlMap | null => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
  );
};

type InventoryItemOfOrderItem = Omit<RewareAdminTypes.InventoryItemAdmin, 'imageUrls'> & {
  imageUrls?: ImageUrlMap | null;
};

export type OrderItem = Omit<RewareAdminTypes.OrderItemAdmin, 'inventoryItem'> & {
  inventoryItem?: InventoryItemOfOrderItem | null;
};

/**
 * One row as it arrives, before the boundary parse. Narrowed on `paymentDetails` for the same
 * reason {@link GiftCardAmount} is: the generated `OrderAdmin` carries the full `GiftcardAdmin`,
 * so without this `page.rows[0].paymentDetails.paymentGiftCards[0].number` type-checks — inside
 * the very file whose comment says that value must never reach the browser.
 */
export type RawOrder = Omit<RewareAdminTypes.OrderAdmin, 'paymentDetails'> & {
  paymentDetails?: PaymentDetails | null;
};

/**
 * The page envelope as it arrives — every field nullable, rows a nullable list of nullable
 * elements. See {@link OrdersResult} for the parsed form `fetchOrders` hands back.
 */
export type OrdersPage = Omit<RewareAdminTypes.PagedOrderAdmin, 'rows'> & {
  rows?: (RawOrder | null)[] | null;
};

/**
 * One order as the page renders it: the generated `OrderAdmin` with `deliveryAddress` replaced
 * by the parsed form. The raw field is an opaque JSON scalar (`any`), so leaving it in place
 * would silently un-type every `order.deliveryAddress?.x` read on the page.
 */
export type OrderRecord = Omit<
  RewareAdminTypes.OrderAdmin,
  'deliveryAddress' | 'paymentDetails' | 'orderItems'
> & {
  deliveryAddress: DeliveryAddress | null;
  paymentDetails?: PaymentDetails | null;
  orderItems?: (OrderItem | null)[] | null;
};

/**
 * What `fetchOrders` returns once the envelope has been validated: the narrowing the wire type
 * cannot express. Keeping the two distinct is what stops callers re-deriving the null handling.
 */
export interface OrdersResult {
  count: number;
  rows: OrderRecord[];
}

/**
 * Drop the nulls that every `[Type]` list in the admin schema can contain, and treat an absent
 * list as empty.
 *
 * The hand-written types this file replaced declared these arrays as `T[]`, so every consumer
 * mapped over them assuming non-null elements — one null row from the API would have thrown
 * inside a render. Use this at each consumption site rather than re-asserting.
 */
export const present = <T>(list: (T | null)[] | null | undefined): T[] =>
  (list ?? []).filter((item): item is T => item != null);

/**
 * reware-api's tri-state filter encoding: 1 = yes, 2 = no, omitted = either. Not a boolean, and
 * not 1/0: the orders resolver tests `=== 1` / `=== 2` explicitly, so a 0 matches neither branch
 * and the filter is silently ignored while the UI still shows the chip. The generated input types
 * these as plain `Int`; this narrows that to the two values the resolver actually reads.
 */
export type TriState = 1 | 2;

/**
 * Mirrors the generated `OrderFiltersInputTypeAdmin`, with the three fields we constrain further
 * and the four we always send made required.
 */
export interface FetchOrdersFilters
  extends Pick<
    RewareAdminTypes.OrderFiltersInputTypeAdmin,
    | 'state'
    | 'ypackageState'
    | 'orderNumber'
    | 'orderId'
    | 'orderUuid'
    | 'externalOrderNumber'
    | 'email'
    | 'firstName'
    | 'lastName'
    | 'refundState'
    | 'commercePlatform'
  > {
  partnerId: number;
  page: number;
  /**
   * Must always be sent. The API defaults to 10, so omitting it silently desyncs react-admin's
   * page arithmetic from the server's and makes the tail of the result set unreachable.
   */
  pageSize: number;
  /** The server orders by placed date only; the sort *field* is ignored. */
  sort: 'newest' | 'oldest';
  captured?: TriState;
  hasReturns?: TriState;
  hasEscalations?: TriState;
}

export interface OrderConstants {
  states: ConstantOption[];
  ypackageStates: ConstantOption[];
  conditions: ConstantOption[];
  refundReasons: ConstantOption[];
  giftcardReasons: ConstantOption[];
  /** Problem-order return reasons — `reason` is required on OrderReturnItemAdmin. */
  returnReasons: ConstantOption[];
  freeShippingReasons: ConstantOption[];
}
